import React, { useEffect, useState } from "react";
import { Card, Table, Button, Row, Col, Form, Image, Pagination } from '@themesberg/react-bootstrap';
import { Link } from "react-router-dom";
import api from "../../utils/api";
import Swal from "sweetalert2";
import defaultIcon from "./../../assets/img/brand/logo2.png";

export default function GroupsList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState("");
  const baseURL = process.env.REACT_APP_API_URL;
  /* console.log("Base url ",baseURL); */


  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/groups`, { params: { page, perPage, search } });
      if (!data.error) {
        setItems(data.data);
        setTotal(data.total);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [page, perPage]);


  // ---------------- BLOCK GROUP ----------------
  const blockGroup = async (id) => {
    try {
      const { data } = await api.post(`/groups/${id}/block`);
      return data;
    } catch (e) {
      return { error: true, message: e?.response?.data?.message || e.message };
    }
  };

  const unblockGroup = async (id) => {
    try {
      const { data } = await api.post(`/groups/${id}/unblock`);
      return data;
    } catch (e) {
      return { error: true, message: e?.response?.data?.message || e.message };
    }
  };

  const handleBlock = async (group) => {
    const result = await Swal.fire({
      title: `Block ${group.title}?`,
      text: "This group will be blocked.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#3b82f6",
      confirmButtonText: "Yes, Block",
      heightAuto: false,
    });

    if (!result.isConfirmed) return;

    const response = await blockGroup(group.id);
    if (!response.error) {
      Swal.fire({
        icon: "success",
        title: "Blocked!",
        text: response.message,
        timer: 1500,
        showConfirmButton: false,
      });
      fetchData();
    } else {
      Swal.fire({ icon: "error", title: "Block Failed", text: response.message });
    }
  };

  const handleUnblock = async (group) => {
    const result = await Swal.fire({
      title: `Unblock ${group.title}?`,
      text: "This group will be active again.",
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#3b82f6",
      confirmButtonText: "Yes, Unblock",
      heightAuto: false,
    });

    if (!result.isConfirmed) return;

    const response = await unblockGroup(group.id);
    if (!response.error) {
      Swal.fire({
        icon: "success",
        title: "Unblocked!",
        text: response.message,
        timer: 1500,
        showConfirmButton: false,
      });
      fetchData();
    } else {
      Swal.fire({ icon: "error", title: "Unblock Failed", text: response.message });
    }
  };


  // ---------------- DELETE GROUP ----------------
  const handleDelete = async (id) => {
    const { data } = await api.get(`/groups/${id}`);
    const g = data.data || {};

    const result = await Swal.fire({
      title: `Delete ${g.title}?`,
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      heightAuto: false
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/groups/${id}`);
      await Swal.fire({
        title: 'Deleted!',
        text: 'Group deleted successfully.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
        heightAuto: false
      });
      fetchData();
    } catch (e) {
      Swal.fire({
        title: "Error",
        text: e?.response?.data?.message || e.message || "Failed to delete",
        icon: "error",
        heightAuto: false
      });
    }
  };

  // ---------------- VIEW GROUP ----------------
  const handleView = async (id) => {
    try {
      const { data } = await api.get(`/groups/${id}`);
      const g = data.data || {};

      const members = g.groupMembers || [];
      const activeMembers = members.filter(m => m.status === "active");

      // ---- Build Members HTML Table ----
      const contentHtml = `
  <div style="text-align:center;margin-bottom:10px;">
  ${
    g.uri
      ? `<img 
          src="${g.uri.startsWith('http') ? g.uri : '/uploads/' + g.uri}" 
          onerror="this.onerror=null; this.src='${defaultIcon}';"
          style="width:130px;height:90px;object-fit:cover;border-radius:8px;margin-bottom:6px;" 
        />`
      : `<img 
          src="${defaultIcon}" 
          style="width:130px;height:90px;object-fit:cover;border-radius:8px;margin-bottom:6px;" 
        />`
  }
</div>

  <h3 style="margin:0;font-size:20px;">${g.name}</h3>

  <div style="font-size:14px;line-height:1.4;margin-top:8px;margin-bottom:12px;">
    <p style="margin:3px 0;"><b>ID:</b> ${g.id}</p>
    <p style="margin:3px 0;"><b>Total Members:</b> ${members.length}</p>
    <p style="margin:3px 0;"><b>Active:</b> ${activeMembers.length}</p>
    <p style="margin:3px 0;"><b>Status:</b> <span style="color:${g.isBlocked ? "red" : "green"};">${g.isBlocked ? "Blocked" : "Active"}</span></p>
    <p style="margin:3px 0;"><b>Created:</b> ${g.createdAt ? new Date(g.createdAt).toLocaleString() : '-'}</p>
  </div>

  <hr style="margin:8px 0;" />

  <h4 style="margin:4px 0 8px;">Members</h4>

  <div style="max-height:230px;overflow-y:auto;border:1px solid #ddd;border-radius:5px;">
    <table style="width:100%;border-collapse:collapse;font-size:13px;">
      <thead>
        <tr style="background:#f3f4f6;">
          <th style="padding:6px;border:1px solid #ddd;">Photo</th>
          <th style="padding:6px;border:1px solid #ddd;">Name</th>
          <th style="padding:6px;border:1px solid #ddd;">Role</th>
          <th style="padding:6px;border:1px solid #ddd;">Status</th>
          <th style="padding:6px;border:1px solid #ddd;">Joined</th>
        </tr>
      </thead>
      <tbody>
        ${members.map(m => `
          <tr>
            <td style="padding:6px;border:1px solid #ddd;text-align:center;">
  ${
    m.user?.uri
      ? `<img 
           src="${m.user.uri.startsWith('http') ? m.user.uri : '/uploads/' + m.user.uri}" 
           onerror="this.onerror=null; this.src='${defaultIcon}';" 
           style="width:34px;height:34px;border-radius:50%;object-fit:cover;" 
         />`
      : `<img 
           src="${defaultIcon}" 
           style="width:34px;height:34px;border-radius:50%;object-fit:cover;" 
         />`
  }
</td>
            <td style="padding:6px;border:1px solid #ddd;">${m.user?.name || '-'}</td>
            <td style="padding:6px;border:1px solid #ddd;">${m.role}</td>
            <td style="padding:6px;border:1px solid #ddd;">${m.status}</td>
            <td style="padding:6px;border:1px solid #ddd;">${m.joined_at ? new Date(m.joined_at).toLocaleString() : '-'}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>
  </div>
`;


      await Swal.fire({
        title: false,
        html: contentHtml,
        // icon: "info",
        width: 700,
        heightAuto: false,
        showCloseButton: true
      });

    } catch (e) {
      Swal.fire({
        title: "Error",
        text: e?.response?.data?.message || e.message || "Failed to load group",
        // icon: "error",
        heightAuto: false
      });
    }
  };


  // ---------------- Pagination ----------------
  const totalPages = Math.ceil(total / perPage) || 1;


  return (
    <Card className="shadow-sm">
      <Card.Header>
        {/* <Row className="align-items-center">
          <Col><h5 className="mb-0">Groups List</h5></Col>
          <Col className="text-end">
            <Button as={Link} to={`/groups/new`} variant="primary">Create Group</Button>
          </Col>
        </Row> */}

        <Row className="mt-3">
          <Col md={6}>
            <Form.Control
              placeholder="Search groups..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Button variant="outline-secondary" onClick={() => { setPage(1); fetchData(); }}>Search</Button>
          </Col>
        </Row>
      </Card.Header>

      <Card.Body>
        <Table responsive hover>
          <thead>
            <tr>
              <th>#</th>
              <th>Cover</th>
              <th>Creator Name</th>
              <th>Group Title</th>
              <th>Members</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {items.map((g, index) => (
              <tr key={g.id}>
                <td>{(page - 1) * perPage + index + 1}</td>
                <td>
                  <Image
                      src={g.uri || defaultIcon}
                      width={50}
                      height={'auto'}
                      rounded
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = defaultIcon;
                      }}
                    />
                </td>
                <td>{g.creator?.name || "-"}</td>
                <td>{g.name}</td>
                <td>{g.memberCount || 0}</td>
                <td>{g.isBlocked ? "Blocked" : "Active"}</td>

                <td>
                  <Button size="sm" variant="outline-info" className="me-2" onClick={() => handleView(g.id)}>View</Button>
                  {/*  <Button size="sm" variant="outline-secondary" className="me-2" as={Link} to={`/groups/${g.id}/edit`}>Edit</Button>
                  <Button size="sm" variant="outline-danger" className="me-2" onClick={() => handleDelete(g.id)}>Delete</Button> */}

                  {/* {g.isBlocked ? (
                    <Button size="sm" variant="outline-success" onClick={() => handleUnblock(g)}>Unblock</Button>
                  ) : (
                    <Button size="sm" variant="outline-warning" onClick={() => handleBlock(g)}>Block</Button>
                  )} */}
                </td>
              </tr>
            ))}

            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center">{loading ? "Loading..." : "No groups found"}</td></tr>
            )}
          </tbody>
        </Table>

        <div className="d-flex justify-content-between align-items-center">
          <Form.Select
            style={{ maxWidth: 120 }}
            value={perPage}
            onChange={(e) => { setPerPage(parseInt(e.target.value)); setPage(1); }}
          >
            {[10, 20, 50].map(n => <option key={n} value={n}>{n} / page</option>)}
          </Form.Select>

          <Pagination className="mb-0">

            {/* Prev */}
            <Pagination.Prev
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            />

            {/* Page numbers: previous */}
            {page > 1 && (
              <Pagination.Item onClick={() => setPage(page - 1)}>
                {page - 1}
              </Pagination.Item>
            )}

            {/* Active page */}
            <Pagination.Item active>{page}</Pagination.Item>

            {/* Page numbers: next */}
            {page < totalPages && (
              <Pagination.Item onClick={() => setPage(page + 1)}>
                {page + 1}
              </Pagination.Item>
            )}

            {/* Next */}
            <Pagination.Next
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            />

          </Pagination>
        </div>
      </Card.Body>
    </Card>
  );
}
